import { useCallback, useEffect, useState } from 'react'
import type { CenarioExtracao, EtapaDecisaoExtracao, EtapaXabcde, ModoExperienciaExtracao } from '../dominio/didatico/cenariosExtracao'
import {
  atualizarProntidao,
  avaliarEtapaXabcde,
  criarSessaoExtracao,
  decidirMomentoRetirada,
  declararAtencaoInicial,
  declararClassificacao,
  liberarDebriefing,
  navegarEtapaExtracao,
  registrarJustificativa,
  registrarOrdemRetirada,
  selecionarVitimaSessao,
  trocarCenarioExtracao,
  trocarModoExtracao,
  type PrioridadeDeclarada,
  type ProntidaoExtracao,
  type SessaoExtracao,
} from '../dominio/didatico/sessaoExtracao'
import { desserializarSessaoExtracao, serializarSessaoExtracao } from '../dominio/didatico/sessaoExtracaoSerializacao'

const CHAVE_SESSAO_EXTRACAO_LOCAL = 'csv-lab-sessao-extracao-v1'

/** Mesmo espírito de `carregarTabletopLocal`: sem `window` (SSR/teste) ou com dado inválido, começa do zero. */
function carregarSessaoExtracaoLocal(extras: readonly CenarioExtracao[]): SessaoExtracao {
  if (typeof window === 'undefined') return criarSessaoExtracao()
  try {
    const salvo = window.localStorage.getItem(CHAVE_SESSAO_EXTRACAO_LOCAL)
    return salvo ? desserializarSessaoExtracao(salvo, extras) : criarSessaoExtracao()
  } catch {
    return criarSessaoExtracao()
  }
}

export interface ControleSessaoExtracao {
  sessao: SessaoExtracao
  escolherModo: (modo: ModoExperienciaExtracao) => void
  escolherCenario: (id: string) => void
  navegar: (etapa: EtapaDecisaoExtracao) => void
  focarVitima: (id: string) => void
  avaliar: (etapa: EtapaXabcde) => void
  justificar: (etapa: EtapaDecisaoExtracao, texto: string) => void
  preparar: (alteracao: Partial<ProntidaoExtracao>, descricao: string) => void
  decidirMomento: (retirarAgora: boolean) => void
  escolherOrdem: (vitimaId: string, justificativa?: string) => void
  /** Fase 5B */
  declararClassificacao: (vitimaId: string, prioridade: PrioridadeDeclarada, justificativa: string) => void
  declararAtencaoInicial: (vitimaId: string, justificativa: string) => void
  liberarDebriefing: () => void
}

/**
 * `cenariosAutorais` são cenários fora do catálogo fixo (Fase 5B) — passados
 * pelo chamador (`App.tsx`, que já os carrega via `useCenariosAutorais`) pra
 * esta sessão conseguir resolver um `cenarioId` autoral tanto na carga
 * inicial quanto ao trocar de cenário.
 */
export function useSessaoExtracao(cenariosAutorais: readonly CenarioExtracao[] = []): ControleSessaoExtracao {
  const [sessao, setSessao] = useState(() => carregarSessaoExtracaoLocal(cenariosAutorais))

  // autosave com debounce — a sessão continua utilizável se o navegador
  // bloquear o armazenamento (ex.: modo privado), mesmo padrão de useTabletop
  useEffect(() => {
    const agendamento = window.setTimeout(() => {
      try {
        window.localStorage.setItem(CHAVE_SESSAO_EXTRACAO_LOCAL, serializarSessaoExtracao(sessao))
      } catch {
        // sem persistência; a sessão continua funcionando normalmente
      }
    }, 250)
    return () => window.clearTimeout(agendamento)
  }, [sessao])

  return {
    sessao,
    escolherModo: useCallback((modo) => setSessao((atual) => trocarModoExtracao(atual, modo)), []),
    escolherCenario: useCallback(
      (id) => setSessao((atual) => trocarCenarioExtracao(atual, id, cenariosAutorais)),
      [cenariosAutorais],
    ),
    navegar: useCallback((etapa) => setSessao((atual) => navegarEtapaExtracao(atual, etapa)), []),
    focarVitima: useCallback((id) => setSessao((atual) => selecionarVitimaSessao(atual, id)), []),
    avaliar: useCallback((etapa) => setSessao((atual) => avaliarEtapaXabcde(atual, etapa)), []),
    justificar: useCallback((etapa, texto) => setSessao((atual) => registrarJustificativa(atual, etapa, texto)), []),
    preparar: useCallback((alteracao, descricao) => setSessao((atual) => atualizarProntidao(atual, alteracao, descricao)), []),
    decidirMomento: useCallback((retirarAgora) => setSessao((atual) => decidirMomentoRetirada(atual, retirarAgora)), []),
    escolherOrdem: useCallback((id, justificativa) => setSessao((atual) => registrarOrdemRetirada(atual, id, justificativa)), []),
    declararClassificacao: useCallback(
      (vitimaId, prioridade, justificativa) =>
        setSessao((atual) => declararClassificacao(atual, vitimaId, prioridade, justificativa)),
      [],
    ),
    declararAtencaoInicial: useCallback(
      (vitimaId, justificativa) => setSessao((atual) => declararAtencaoInicial(atual, vitimaId, justificativa)),
      [],
    ),
    liberarDebriefing: useCallback(() => setSessao((atual) => liberarDebriefing(atual)), []),
  }
}
